import { getEventValue, validateField, workaroundFieldLabelOverlap } from "components/form/utils/validate-utils";
import { TextField, Box, FormControl, FormLabel, FormHelperText, IconButton, FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import InputError from "components/form/fields/InputError";
import { useFieldInfo } from "contexts/FieldInfoContext";
import { useTranslator } from "hooks/translator";
import { useEffect, useState } from "react";
import Menu from '@mui/material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  YEARS,
  MONTHS,
  WEEKS,
  DAYS,
  HOURS,
  MINUTES,
  SECONDS,
  MILLIS,
  periodToIso,
  isoToPeriod
} from "utils/period";

const DURATION = "duration"
const PERIOD   = "period"

const UNITS = {
  [DURATION]: [HOURS, MINUTES, SECONDS, MILLIS],
  [PERIOD]  : [YEARS, MONTHS, WEEKS, DAYS, HOURS, MINUTES, SECONDS, MILLIS],
}

function toRuntimeValue(value) {
  try {
    return periodToIso(value)
  } catch (error) {
    return null
  }
}

function toComponentValue(value) {
  try {
    return isoToPeriod(value) || {}
  } catch (error) {
    return {}
  }
}

const InputPeriodDurationField = (props) => (
  <InputError>
    <InputPeriodDurationFieldContent {...props} />
  </InputError>
)

const InputPeriodDurationFieldContent = (props) => {
  const { type }             = props
  const { augProps, fieldProps, info } = useFieldInfo()
  const [value, setValue]              = useState(() => isoToPeriod(fieldProps.value) || {})
  const [units, setUnits]              = useState(() => defaultUnits(type, value))

  //synchronizeValues(value, fieldProps.value, toFormValue, toControlValue)

  // when the form value changes, update the component value as well
  useEffect(() => {
    if (fieldProps.value != toRuntimeValue(value)) {
      setValue(toComponentValue(fieldProps.value))
    }
  }, [fieldProps.value])

  // when the component value changes, update the form value as well
  useEffect(() => {
    const formValue = toRuntimeValue(value)
    if (augProps.touched) handleValidate(null, formValue)
    augProps.setValue(formValue)
  }, [value])

  function handleValidate(e, value) {
    const error = validateField("period", fieldProps.required, e, value)
    augProps.setError(error)
  }

  function handleChange (e, unit) {
    const eventValue = getEventValue(e, null)
    const num        = parseInt(eventValue, 10)
    const val        = Number.isNaN(num) ? null : num

    setValue(period => ({...period, [unit]: val}))
  }

  return (
    <FormControl id={fieldProps.id} sx={{width: "100%"}} component="fieldset" required={fieldProps.required} error={fieldProps.error}>
      {!info.inMultiple ? <FormLabel sx={{marginBottom: "5px"}} component="legend" >{fieldProps.label}</FormLabel> : null}

      <Box sx={{display: "flex", flexDirection: "row", alignItems: "center"}}>
        <DurationFields type={type} onChange={handleChange} units={units} period={value} />
        <Box sx={{flexShrink: 0}}>
          <UnitSelector type={type} units={units} setUnits={setUnits} setPeriod={setValue}/>
        </Box>
      </Box>

      { !info.inMultiple ? <FormHelperText >{fieldProps.error ? fieldProps.helperText : " "}</FormHelperText> : null }
    </FormControl>
  )
}

const DurationFields = ({type, units, onChange, period}) => {
  const { info } = useFieldInfo()
  return (
    <Box sx={{display: "flex", flexDirection: "row", flexGrow: 1}}>
      { UNITS[type].map((unit, index) => units[unit]
          ? <InputField rpath={`${info.rpath}_${unit}`} key={index} unit={unit} units={units} onChange={onChange} period={period}/>
          : null
        )
      }
    </Box>
  )
}

const InputField = ({rpath, unit, onChange, period}) => {
  const { fieldProps } = useFieldInfo()
  const workaround     = workaroundFieldLabelOverlap(rpath)
  const {t}            = useTranslator()

  return (
    <TextField
      sx={{flexGrow: 1, minWidth: "70px"}}

      {...fieldProps}
      {...workaround}

      value={period[unit] == null ? "" : period[unit]}

      onWheel={e => e.currentTarget.blur()}

      id={rpath}
      name={rpath}
      label={t(unit)}
      onChange={(e) => onChange(e, unit)}
      helperText={undefined}
      placeholder={undefined}
      required={false}
      margin="none"
      type="number"
    />
  )
}

const UnitSelector = ({type, units, setUnits, setPeriod}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslator()
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpdate = (unit) => {
    setUnits(units => {
      const active = units[unit]
      if (active)
        setPeriod(period => ({...period, [unit]: null}))

      return {...units, [unit]: !units[unit] }
    })
  }

  const UnitCheckboxes = () => {
    return UNITS[type].map((unit, index) => (
      <FormControlLabel
        onClick={() => handleUpdate(unit)}
        key={index}
        label={t(unit)}
        control={<Checkbox checked={units[unit]}/>}
      />
    ))
  }

  return (
    <>
      <IconButton id="test" size={"medium"} tabIndex={-1} onClick={handleClick}  >
        <ArrowDropDownIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <FormGroup sx={{paddingLeft: "10px", paddingRight: "10px"}}>
          <UnitCheckboxes/>
        </FormGroup>
      </Menu>
    </>
  );
}

function defaultUnits (type, period) {
  const active = Object.fromEntries(UNITS[type].map(unit => [unit, false]))
  const activate = (unit) => active[unit] = true
  switch (type) {
    case DURATION:
      activate(SECONDS)
      activate(MINUTES)
      activate(HOURS)
      break;
    case PERIOD:
      activate(SECONDS)
      activate(MINUTES)
      activate(HOURS)
      break;
    default:
  }

  // also activate fields that are in the initial input
  Object.entries(period).forEach(([k, v]) => {
    if (v != undefined && v != null)
      activate(k)
  })

  return active
}

export default InputPeriodDurationField
